import { useAppDispatch, useAppSelector } from 'hooks';
import { useEffect } from 'react';
import {
  selectPersistenceDurationMilliseconds,
  selectUpdatedAtMilliseconds,
} from 'reducers/workflows';
import { clearConversation } from 'actions/workflows-v2/workflowsV2';

// This hook restarts a `setTimeout()` on every interaction with the widget.
// If user doesn't interact with the widget for the duration of persistence
// window, the latest timeout will be reached, and the conversation will be
// cleared and restarted in the primary tab. Secondary tabs will receive new
// state from localStorage.
// This hook will execute regardless of `persistence_enabled` value.
export function useClearConversationOnTimeout(): void {
  const dispatch = useAppDispatch();
  const updatedAtMilliseconds = useAppSelector(selectUpdatedAtMilliseconds);
  const persistenceDurationMilliseconds = useAppSelector(
    selectPersistenceDurationMilliseconds,
  );

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    /**
     * Implement `setInterval()` using recursive call to `setTimeout()`.
     *
     * {@link https://developer.mozilla.org/en-US/docs/Web/API/setInterval#ensure_that_execution_duration_is_shorter_than_interval_frequency}
     */
    (function loop() {
      timeout = setTimeout(async () => {
        const isExpired =
          Date.now() - updatedAtMilliseconds > persistenceDurationMilliseconds;

        if (isExpired) {
          dispatch(
            clearConversation({
              reason: 'clear_conversation_on_timeout',
            }),
          );
          return;
        }

        loop();
      }, 1000);
    })();

    return () => {
      clearTimeout(timeout);
    };
  }, [dispatch, persistenceDurationMilliseconds, updatedAtMilliseconds]);
}
